import styled from 'styled-components';
import { ChipStyleMixin } from 'shared/styles';
import { CollectorStateType, SurveyStateTypes } from 'shared/types';

export const DrawerBody = styled.div`
  padding: 15px;
  height: 100%;

  &.edit-response {
    height: calc(100% - 60px);
    overflow: auto;

    label {
      font-size: 14px !important;
      padding-bottom: 6px;
      font-weight: 500;
    }
  }

  .end-max-length {
    opacity: 1 !important;
  }

  &.all-list {
    background: var(--color-smoke-50);
    overflow: auto;
    .category-menu__section {
      border-radius: 2px;
      background-color: #fff;
      box-shadow: 0 1px 1px 0 #dbdfe3;
      padding: 10px;
      min-height: 100%;

      .category-menu__section-header {
        padding: 5px;
        margin-bottom: 8px;
        label {
          font-weight: 600;
          font-size: 12px;
          line-height: 20px;
        }
      }
    }

    .loadnew {
      margin: 10px;
      width: auto;
      display: block;
      padding: 4px 15px 5px;
      cursor: pointer;
      font-weight: 500;
      letter-spacing: 0.3px;
      border: 1px solid #cfd7df;
      border-radius: 4px;
      background-color: #fff;
      font-size: 14px;
      background-image: linear-gradient(#fff 0%, #f5f7f9 100%);
      box-shadow: #1832470d 0px 1px 0px 0px;
      color: #12344d;
      height: 34px;

      &:hover {
        background-color: rgb(245, 247, 249);
        background-image: none;
      }
    }
  }

  .edit-deleted {
    margin: 18px 0px;
    .deleted-text {
      font-size: 12px;
      background-color: #ebeff3;
      padding: 5px 15px;
      border-radius: 5px;
      color: #384551;
      font-weight: 600;
      margin: 5px 0px;
      width: fit-content;
    }
    .deleted-question {
      font-size: 14px;
      opacity: 70%;
    }
    .deleted-answer {
      color: #12344d;
      opacity: 50%;
      font-size: 14px;
      margin: 5px 0;
    }
  }

  .empty {
    text-align: center;
    color: var(--secondary-color);
  }
`;

export const DrawerFooter = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  justify-content: flex-end;
  display: flex;
  padding: 12px 16px 12px 16px;
  border-top: 1px solid var(--border-color);
  background: #fff;
  border-radius: 4px;
`;

interface IValueBox {
  rating:
    | 'promoter'
    | 'detractor'
    | 'passive'
    | 'loading'
    | 'positive'
    | 'neutral'
    | 'negative';
}

const RATING_TYPES: any = {
  promoter: {
    bg: '#DEEEE4',
    color: '#058538',
    border: '#A7D8BB',
  },
  positive: {
    bg: '#DEEEE4',
    color: '#058538',
    border: '#A7D8BB',
  },
  passive: {
    bg: '#FFF1DD',
    color: '#DB9815',
    border: '#F2D9B3',
  },
  neutral: {
    bg: '#FFF1DD',
    color: '#DB9815',
    border: '#F2D9B3',
  },
  detractor: {
    bg: '#FAEAEA',
    color: '#C82124',
    border: '#FACECF',
  },
  negative: {
    bg: '#FAEAEA',
    color: '#C82124',
    border: '#FACECF',
  },
  loading: {
    bg: 'var(--border-color)',
    color: 'var(--border-color)',
    border: 'var(--border-color)',
  },
};

export const ValueBox = styled.div<IValueBox>`
  ${ChipStyleMixin}
  font-weight: 600;
  text-align: center;
  background: ${(props) => RATING_TYPES[props.label]?.bg};
  color: ${(props) => RATING_TYPES[props.label]?.color};
  min-width: 30px;
  max-width: 30px;
  border-radius: 4px;
  border: 1px solid ${(props) => RATING_TYPES[props.label]?.border};
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.extended {
    min-width: 50px !important;
    max-width: 50px !important;
    height: 50px !important;
    font-weight: bold;
    font-size: 26px;
    line-height: 42px;
  }
`;

export const AlertBox = styled.div`
  padding: 12px 20px;
  background: #ffe2e2;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  color: #12344d;
  align-items: center;
  margin-bottom: 12px;

  .label {
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 5px;
  }

  .message {
    font-size: 14px;
  }
`;

export const StyledMenuItem = styled.div`
  list-style: none;
  color: var(--base-color);
  cursor: pointer;
  padding: 10px 15px;
  display: flex;
  width: 100%;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 5px;
  align-items: center;
  justify-content: space-between;
  line-height: 14px;

  .surveyName {
    word-break: break-word;

    &.DRAFT {
      opacity: 0.5;
    }
  }

  &.selected {
    color: var(--base-color);
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    position: relative;
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    background: #e5f2fd;
    border-radius: 4px;
  }

  &:hover {
    color: var(--base-color);
    background-color: #f4f4f4;
    text-decoration: none;
  }
`;

const STATE_TYPES: {
  [key in CollectorStateType]: {
    bg: string;
    color: string;
    border: string;
  };
} = {
  BLOCKED: {
    bg: '#FFECF0',
    color: '#cb162d',
    border: '#FFD0D6',
  },
  PAUSE: {
    bg: '#FEF1E1',
    color: '#C13E1B',
    border: '#F2D9B3',
  },
  ACTIVE: {
    bg: '#E0F5F1',
    color: '#00795B',
    border: '#B4E5DA',
  },
  SCHEDULED: {
    bg: '#E5F2FD',
    color: '#2C5CC5',
    border: '#BBDCFE',
  },
  DRAFT: {
    bg: '#EBEFF3',
    color: '#384551',
    border: '#EBEFF3',
  },
  COMPLETE: {
    bg: '#FFFFFF',
    color: '#12344D',
    border: '#CFD7DF',
  },
};

type SurveyStateType = {
  statename: SurveyStateTypes | CollectorStateType;
};

export const SurveyState = styled.div<SurveyStateType>`
  ${ChipStyleMixin}
  font-weight: 600;
  text-align: center;
  display: inline-block;
  white-space: nowrap;
  margin-bottom: 2px;
  background: ${(props) => STATE_TYPES[props.statename].bg};
  color: ${(props) => STATE_TYPES[props.statename].color};
  border-radius: 4px;
  border: 1px solid ${(props) => STATE_TYPES[props.statename].border};
  display: inline-flex;
  align-items: center;
  width: auto;

  .icon {
    margin-top: 2px;
    margin-right: 3px;
  }
`;
